import React, { useState, useEffect, useRef } from 'react'
import {
  FaSave,
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaUser,
  FaBuilding,
  FaUsers,
  FaUpload,
  FaLink,
} from 'react-icons/fa'
import aboutService from '../services/aboutService'
import settingsService from '../services/settingsService'
import api from '../services/api'
import Loader from './Loader'
import { toast } from 'react-toastify'

const EMPTY_TEAM_FORM = {
  name: '',
  role: '',
  bio: '',
  image_url: '',
  email: '',
  linkedin_url: '',
  twitter_url: '',
  display_order: 0,
}

const AboutSettings = () => {
  const [subTab, setSubTab] = useState('about')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // About Us fields
  const [aboutForm, setAboutForm] = useState({
    company_name: '',
    company_tagline: '',
    about_mission: '',
    about_vision: '',
    about_story: '',
    about_established: '',
    about_tours_count: '',
    about_customers_count: '',
    about_destinations_count: '',
    about_awards: '',
    social_facebook: '',
    social_twitter: '',
    social_instagram: '',
    social_linkedin: '',
    social_youtube: '',
    social_whatsapp: '',
  })

  // Team members
  const [teamMembers, setTeamMembers] = useState([])
  const [showTeamModal, setShowTeamModal] = useState(false)
  const [editingMember, setEditingMember] = useState(null)
  const [teamForm, setTeamForm] = useState(EMPTY_TEAM_FORM)
  const [teamSaving, setTeamSaving] = useState(false)
  const [imageMode, setImageMode] = useState('url') // 'url' | 'upload'
  const [uploadingImage, setUploadingImage] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (subTab === 'about') {
      fetchAbout()
    } else {
      fetchTeam()
    }
  }, [subTab])

  const fetchAbout = async () => {
    setLoading(true)
    try {
      const data = await aboutService.getAboutContent()
      if (data.success) {
        const a = data.about
        setAboutForm({
          company_name: a.company_name || '',
          company_tagline: a.tagline || '',
          about_mission: a.mission || '',
          about_vision: a.vision || '',
          about_story: a.story || '',
          about_established: a.established || '',
          about_tours_count: a.tours_count || '',
          about_customers_count: a.customers_count || '',
          about_destinations_count: a.destinations_count || '',
          about_awards: a.awards || '',
          social_facebook: a.social_facebook || '',
          social_twitter: a.social_twitter || '',
          social_instagram: a.social_instagram || '',
          social_linkedin: a.social_linkedin || '',
          social_youtube: a.social_youtube || '',
          social_whatsapp: a.social_whatsapp || '',
        })
      }
    } catch (err) {
      toast.error('Failed to load About Us content')
    } finally {
      setLoading(false)
    }
  }

  const fetchTeam = async () => {
    setLoading(true)
    try {
      const data = await aboutService.getTeamMembers()
      setTeamMembers(data.team || [])
    } catch (err) {
      toast.error('Failed to load team members')
    } finally {
      setLoading(false)
    }
  }

  const handleAboutChange = (field, value) => {
    setAboutForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSaveAbout = async () => {
    setSaving(true)
    try {
      await settingsService.updateMultipleSettings(aboutForm)
      toast.success('About Us content saved successfully')
    } catch (err) {
      toast.error('Failed to save About Us content')
    } finally {
      setSaving(false)
    }
  }

  // ---- Team modal helpers ----
  const openCreateTeam = () => {
    setEditingMember(null)
    setTeamForm(EMPTY_TEAM_FORM)
    setImageMode('url')
    setShowTeamModal(true)
  }

  const openEditTeam = (member) => {
    setEditingMember(member)
    setTeamForm({
      name: member.name || '',
      role: member.role || '',
      bio: member.bio || '',
      image_url: member.image_url || '',
      email: member.email || '',
      linkedin_url: member.linkedin_url || '',
      twitter_url: member.twitter_url || '',
      display_order: member.display_order || 0,
    })
    setImageMode('url')
    setShowTeamModal(true)
  }

  const handleTeamFormChange = (field, value) => {
    setTeamForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleImageFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    const formData = new FormData()
    formData.append('image', file)
    setUploadingImage(true)
    try {
      const response = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (response.data.success) {
        setTeamForm((prev) => ({ ...prev, image_url: response.data.url }))
        toast.success('Image uploaded successfully')
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Image upload failed')
    } finally {
      setUploadingImage(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleSaveTeamMember = async () => {
    if (!teamForm.name.trim() || !teamForm.role.trim()) {
      toast.error('Name and role are required')
      return
    }
    setTeamSaving(true)
    try {
      if (editingMember) {
        await aboutService.updateTeamMember(editingMember.id, teamForm)
        toast.success('Team member updated')
      } else {
        await aboutService.createTeamMember(teamForm)
        toast.success('Team member added')
      }
      setShowTeamModal(false)
      fetchTeam()
    } catch (err) {
      toast.error('Failed to save team member')
    } finally {
      setTeamSaving(false)
    }
  }

  const handleDeleteTeamMember = async (id, name) => {
    if (!window.confirm(`Delete "${name}" from the team?`)) return
    try {
      await aboutService.deleteTeamMember(id)
      toast.success('Team member deleted')
      fetchTeam()
    } catch (err) {
      toast.error('Failed to delete team member')
    }
  }

  return (
    <div>
      {/* Sub-tab bar */}
      <div className="flex gap-3 mb-6 border-b pb-3">
        <button
          onClick={() => setSubTab('about')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
            subTab === 'about'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <FaBuilding />
          About Us Info
        </button>
        <button
          onClick={() => setSubTab('team')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
            subTab === 'team'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <FaUsers />
          Team Members
        </button>
      </div>

      {/* ====================== ABOUT US TAB ====================== */}
      {subTab === 'about' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">About Us Content</h2>
            <button
              onClick={handleSaveAbout}
              disabled={saving}
              className="btn-primary flex items-center gap-2 disabled:opacity-60"
            >
              <FaSave />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <div className="space-y-6">
              {/* Company Identity */}
              <div className="card p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b">
                  Company Identity
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={aboutForm.company_name}
                      onChange={(e) => handleAboutChange('company_name', e.target.value)}
                      className="input-field w-full"
                      placeholder="Company name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
                    <input
                      type="text"
                      value={aboutForm.company_tagline}
                      onChange={(e) => handleAboutChange('company_tagline', e.target.value)}
                      className="input-field w-full"
                      placeholder="Short tagline"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Established Year
                    </label>
                    <input
                      type="text"
                      value={aboutForm.about_established}
                      onChange={(e) => handleAboutChange('about_established', e.target.value)}
                      className="input-field w-full"
                      placeholder="e.g. 2015"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Awards</label>
                    <input
                      type="text"
                      value={aboutForm.about_awards}
                      onChange={(e) => handleAboutChange('about_awards', e.target.value)}
                      className="input-field w-full"
                      placeholder="e.g. Best Tour Operator 2023"
                    />
                  </div>
                </div>
              </div>

              {/* Mission & Vision */}
              <div className="card p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b">
                  Mission & Vision
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mission</label>
                    <textarea
                      rows={4}
                      value={aboutForm.about_mission}
                      onChange={(e) => handleAboutChange('about_mission', e.target.value)}
                      className="input-field w-full"
                      placeholder="Our mission statement..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vision</label>
                    <textarea
                      rows={4}
                      value={aboutForm.about_vision}
                      onChange={(e) => handleAboutChange('about_vision', e.target.value)}
                      className="input-field w-full"
                      placeholder="Our vision statement..."
                    />
                  </div>
                </div>
              </div>

              {/* Our Story */}
              <div className="card p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b">Our Story</h3>
                <textarea
                  rows={6}
                  value={aboutForm.about_story}
                  onChange={(e) => handleAboutChange('about_story', e.target.value)}
                  className="input-field w-full"
                  placeholder="Tell the company story..."
                />
              </div>

              {/* Stats */}
              <div className="card p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b">
                  Key Statistics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tours Count
                    </label>
                    <input
                      type="text"
                      value={aboutForm.about_tours_count}
                      onChange={(e) => handleAboutChange('about_tours_count', e.target.value)}
                      className="input-field w-full"
                      placeholder="e.g. 200+"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customers Count
                    </label>
                    <input
                      type="text"
                      value={aboutForm.about_customers_count}
                      onChange={(e) => handleAboutChange('about_customers_count', e.target.value)}
                      className="input-field w-full"
                      placeholder="e.g. 10,000+"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Destinations Count
                    </label>
                    <input
                      type="text"
                      value={aboutForm.about_destinations_count}
                      onChange={(e) =>
                        handleAboutChange('about_destinations_count', e.target.value)
                      }
                      className="input-field w-full"
                      placeholder="e.g. 50+"
                    />
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="card p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2 border-b">
                  Social Media Links
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'social_facebook', label: 'Facebook URL' },
                    { key: 'social_twitter', label: 'Twitter / X URL' },
                    { key: 'social_instagram', label: 'Instagram URL' },
                    { key: 'social_linkedin', label: 'LinkedIn URL' },
                    { key: 'social_youtube', label: 'YouTube URL' },
                    { key: 'social_whatsapp', label: 'WhatsApp Number' },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {label}
                      </label>
                      <input
                        type="text"
                        value={aboutForm[key]}
                        onChange={(e) => handleAboutChange(key, e.target.value)}
                        className="input-field w-full"
                        placeholder={`Enter ${label.toLowerCase()}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveAbout}
                  disabled={saving}
                  className="btn-success flex items-center gap-2 px-8 py-3 disabled:opacity-60"
                >
                  <FaSave />
                  {saving ? 'Saving...' : 'Save All Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ====================== TEAM MEMBERS TAB ====================== */}
      {subTab === 'team' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Team Members</h2>
            <button onClick={openCreateTeam} className="btn-primary flex items-center gap-2">
              <FaPlus />
              Add Member
            </button>
          </div>

          {loading ? (
            <Loader />
          ) : teamMembers.length === 0 ? (
            <div className="text-center py-16 card">
              <FaUsers className="text-6xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-4">No team members yet</p>
              <button
                onClick={openCreateTeam}
                className="btn-primary flex items-center gap-2 px-5 py-2 mx-auto"
              >
                <FaPlus /> Add First Member
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {teamMembers.map((member) => (
                <div key={member.id} className="card p-5 flex flex-col gap-3">
                  <div className="flex items-center gap-4">
                    {member.image_url ? (
                      <img
                        src={member.image_url}
                        alt={member.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-primary-200"
                        onError={(e) => {
                          e.target.onerror = null
                          e.target.style.display = 'none'
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
                        <FaUser className="text-primary-500 text-2xl" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">{member.name}</h3>
                      <p className="text-primary-600 font-medium text-sm">{member.role}</p>
                    </div>
                  </div>
                  {member.bio && <p className="text-gray-600 text-sm line-clamp-3">{member.bio}</p>}
                  {member.email && <p className="text-gray-500 text-xs">{member.email}</p>}
                  <div className="flex gap-2 mt-auto pt-3 border-t">
                    <button
                      onClick={() => openEditTeam(member)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium"
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteTeamMember(member.id, member.name)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition text-sm font-medium"
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ====================== TEAM MEMBER MODAL ====================== */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">
                {editingMember ? 'Edit Team Member' : 'Add Team Member'}
              </h2>
              <button
                onClick={() => setShowTeamModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={teamForm.name}
                    onChange={(e) => handleTeamFormChange('name', e.target.value)}
                    className="input-field w-full"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role / Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={teamForm.role}
                    onChange={(e) => handleTeamFormChange('role', e.target.value)}
                    className="input-field w-full"
                    placeholder="e.g. Lead Guide"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  rows={3}
                  value={teamForm.bio}
                  onChange={(e) => handleTeamFormChange('bio', e.target.value)}
                  className="input-field w-full"
                  placeholder="Short bio..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Picture
                </label>

                {/* Toggle */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setImageMode('upload')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      imageMode === 'upload'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FaUpload className="text-xs" /> Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageMode('url')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      imageMode === 'url'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FaLink className="text-xs" /> Paste URL
                  </button>
                </div>

                {imageMode === 'upload' ? (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleImageFileChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-400 hover:text-primary-600 transition disabled:opacity-60 w-full justify-center"
                    >
                      <FaUpload />
                      {uploadingImage ? 'Uploading...' : 'Click to choose an image (max 5 MB)'}
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={teamForm.image_url}
                    onChange={(e) => handleTeamFormChange('image_url', e.target.value)}
                    className="input-field w-full"
                    placeholder="https://example.com/photo.jpg"
                  />
                )}

                {/* Live preview */}
                {teamForm.image_url && (
                  <div className="mt-3 flex items-center gap-3">
                    <img
                      src={teamForm.image_url}
                      alt="Preview"
                      className="w-20 h-20 rounded-full object-cover border-2 border-primary-200"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Preview</p>
                      <button
                        type="button"
                        onClick={() => handleTeamFormChange('image_url', '')}
                        className="text-xs text-red-500 hover:text-red-700 transition"
                      >
                        Remove image
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={teamForm.email}
                    onChange={(e) => handleTeamFormChange('email', e.target.value)}
                    className="input-field w-full"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={teamForm.display_order}
                    onChange={(e) =>
                      handleTeamFormChange('display_order', parseInt(e.target.value) || 0)
                    }
                    className="input-field w-full"
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    LinkedIn URL
                  </label>
                  <input
                    type="text"
                    value={teamForm.linkedin_url}
                    onChange={(e) => handleTeamFormChange('linkedin_url', e.target.value)}
                    className="input-field w-full"
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Twitter URL
                  </label>
                  <input
                    type="text"
                    value={teamForm.twitter_url}
                    onChange={(e) => handleTeamFormChange('twitter_url', e.target.value)}
                    className="input-field w-full"
                    placeholder="https://twitter.com/..."
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSaveTeamMember}
                  disabled={teamSaving}
                  className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <FaSave />
                  {teamSaving ? 'Saving...' : editingMember ? 'Update Member' : 'Add Member'}
                </button>
                <button
                  onClick={() => setShowTeamModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AboutSettings
